import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Trash2, Download, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterManager = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSubscribers = useCallback(async () => {
    try {
      const response = await adminApi.get('/api/admin/newsletter/subscribers');
      setSubscribers(response.data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les abonnés", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubscribers(); }, [loadSubscribers]);

  const handleDelete = async (subscriberId) => {
    if (!window.confirm('Supprimer cet abonné ?')) return;
    try {
      await adminApi.delete(`/api/admin/newsletter/subscribers/${subscriberId}`);
      toast({ title: "Abonné supprimé" });
      loadSubscribers();
    } catch (error) {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const exportCSV = () => {
    const csv = "Email,Date d'inscription\n" + subscribers.map(s => `${s.email},${s.subscribedAt}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-400 text-lg">Chargement...</p></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="newsletter-manager-title">Newsletter</h1>
          <p className="text-gray-400">{subscribers.length} abonné(s)</p>
        </div>
        {subscribers.length > 0 && (
          <Button onClick={exportCSV} className="bg-green-600 hover:bg-green-700" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Exporter CSV
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <div className="flex items-center gap-3">
            <div className="bg-purple-600/20 p-3 rounded-lg">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{subscribers.length}</p>
              <p className="text-gray-400 text-sm">Total abonnés</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <div className="flex items-center gap-3">
            <div className="bg-green-600/20 p-3 rounded-lg">
              <Mail className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{subscribers.filter(s => s.active).length}</p>
              <p className="text-gray-400 text-sm">Actifs</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600/20 p-3 rounded-lg">
              <Mail className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {subscribers.filter(s => {
                  const d = new Date(s.subscribedAt);
                  const now = new Date();
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                }).length}
              </p>
              <p className="text-gray-400 text-sm">Ce mois-ci</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscribers List */}
      {subscribers.length === 0 ? (
        <div className="text-center py-20 bg-gray-900 rounded-lg border border-gray-800">
          <Mail className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Aucun abonné pour le moment</p>
        </div>
      ) : (
        <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
          <table className="w-full" data-testid="subscribers-table">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-sm font-medium text-gray-400 px-6 py-4">Email</th>
                <th className="text-left text-sm font-medium text-gray-400 px-6 py-4">Date d'inscription</th>
                <th className="text-left text-sm font-medium text-gray-400 px-6 py-4">Statut</th>
                <th className="text-right text-sm font-medium text-gray-400 px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub) => (
                <tr key={sub.id} className="border-b border-gray-800/50 hover:bg-gray-800/30" data-testid={`subscriber-${sub.id}`}>
                  <td className="px-6 py-4 text-white text-sm">{sub.email}</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">
                    {new Date(sub.subscribedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${sub.active ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                      {sub.active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => handleDelete(sub.id)} className="border-red-700 text-red-500 hover:bg-red-900">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewsletterManager;
